import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { FormSection } from '../../types/questionnaire.types';

interface ProgressBarProps {
  sections: FormSection[];
  currentSectionIndex: number;
  scores?: Record<string, any>;
  isRTL?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ sections, currentSectionIndex, scores = {}, isRTL = false }) => {
  // Detect mobile screen
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Step 1: Collect ALL unique progressBarText values from ALL sections
  // This includes hidden sections (showProgressBar=false) and score-based sections
  // Progress bar displays all potential steps, marking hidden ones as completed
  const allProgressTexts: string[] = [];
  const progressLabels: Record<string, string> = {};
  sections.forEach((section) => {
    const text = section.progressBarText?.trim();
    if (text && !allProgressTexts.includes(text)) {
      allProgressTexts.push(text);
      // Store the label for this progress text (from first section with this text)
      progressLabels[text] = section.progressBarLabel?.trim() || '';
    }
  });

  // If no progress texts at all, don't render
  if (allProgressTexts.length === 0) {
    return null;
  }

  // Step 2: Get current section
  const currentSection = sections[currentSectionIndex];

  // Only show progress bar if current section has showProgressBar = true
  if (!currentSection?.showProgressBar) {
    return null;
  }

  // Current section must have a valid ProgressBarText
  const currentProgressText = currentSection.progressBarText?.trim();
  if (!currentProgressText || !allProgressTexts.includes(currentProgressText)) {
    return null;
  }

  // Step 3: Display ALL ProgressBarText values
  // Show all circles from the beginning, not just up to current
  const displayTexts = allProgressTexts;
  const currentProgressIndex = allProgressTexts.indexOf(currentProgressText);

  // Step 4: Calculate progress percentage
  // Fill bar from start to CENTER of current step circle
  // The circles are positioned with justify-between, so their centers are at these percentages
  const progressPercentage = displayTexts.length > 1
    ? (currentProgressIndex / (displayTexts.length - 1)) * 100
    : 0;

  // Step 5: Render the progress bar
  if (isMobile) {
    // Mobile Design: Horizontal circles with connecting lines, no labels
    const circleSize = 21;
    const lineHeight = 7;

    return (
      <div className="w-full" style={{ padding: '8px 16px' }}>
        <div className="relative" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          {/* Background connecting line (unfilled) */}
          <div
            className="absolute"
            style={{
              left: `${circleSize / 2}px`,
              right: `${circleSize / 2}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              height: `${lineHeight}px`,
              backgroundColor: '#B4D8D6',
              zIndex: 1,
              borderRadius: '4px'
            }}
          />

          {/* Filled connecting line (progress) */}
          <div
            className="absolute transition-all duration-300"
            style={{
              ...(isRTL
                ? { right: `${circleSize / 2}px`, left: 'auto' }
                : { left: `${circleSize / 2}px`, right: 'auto' }
              ),
              width: progressPercentage > 0 ? `calc(${progressPercentage}% - ${circleSize / 2}px)` : '0%',
              top: '50%',
              transform: 'translateY(-50%)',
              height: `${lineHeight}px`,
              backgroundColor: '#3A7380',
              zIndex: 2,
              borderRadius: '4px'
            }}
          />

          {/* Circles */}
          <div
            className="relative flex justify-between items-center"
            style={isRTL ? { flexDirection: 'row-reverse' } : undefined}
          >
            {displayTexts.map((progressText, index) => {
              const isCurrent = index === currentProgressIndex;
              const isCompleted = index < currentProgressIndex;

              return (
                <div
                  key={progressText}
                  className="rounded-full flex items-center justify-center font-semibold"
                  style={{
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                    fontSize: '11px',
                    backgroundColor: (isCompleted || isCurrent) ? '#3A7380' : '#B4D8D6',
                    color: 'white',
                    zIndex: 10,
                    position: 'relative'
                  }}
                >
                  {progressText}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Desktop Design
  return (
    <div className="pt-1 px-4 w-full" style={{ maxWidth: '82%', marginLeft: 'auto', marginRight: 'auto' }}>
      {/* Progress Bar Container */}
      <div className="relative pt-8">
        {/* Background Bar (unfilled) */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-[#B4D8D6] rounded-full" style={{ top: '16px' }} />

        {/* Filled Bar (progress) — fills from right in RTL */}
        <div
          className="absolute top-0 h-2 bg-brand-teal rounded-full transition-all duration-300"
          style={{
            width: `${progressPercentage}%`,
            top: '16px',
            ...(isRTL ? { right: 0, left: 'auto' } : { left: 0 }),
          }}
        />

        {/* Steps - Positioned absolutely for perfect alignment */}
        <div className="relative" style={{ height: '80px' }}>
          {displayTexts.map((progressText, index) => {
            const isCurrent = index === currentProgressIndex;
            const isCompleted = index < currentProgressIndex;

            const rawPosition = displayTexts.length > 1
              ? (index / (displayTexts.length - 1)) * 100
              : 50;
            // In RTL: step 0 is rightmost (100%), step N-1 is leftmost (0%)
            const position = isRTL ? 100 - rawPosition : rawPosition;

            return (
              <div
                key={progressText}
                className="absolute flex flex-col items-center"
                style={{
                  left: `${position}%`,
                  transform: 'translateX(-50%)',
                  top: 0
                }}
              >
                {/* Step Circle */}
                <div
                  className={`
                    min-w-[36px] h-9 px-3 rounded-full flex items-center justify-center font-semibold text-sm z-10 whitespace-nowrap
                    ${isCompleted
                      ? 'bg-white text-brand-teal'
                      : isCurrent
                        ? 'bg-brand-teal text-white'
                        : 'bg-white text-brand-teal'
                    }
                  `}
                >
                  {progressText}
                </div>

                {/* Label below circle */}
                {progressLabels[progressText] && (
                  <div
                    className="mt-2 text-brand-teal font-normal text-center"
                    style={{ fontSize: '15px' }}
                  >
                    {progressLabels[progressText]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
